import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Header from './Header';
import LeftPanel from './LeftPanel';
import RightPanel from './RightPanel';
import GraphCanvas from './GraphCanvas';
import BulkStackToolbar from './BulkStackToolbar';
import AdminPanel from './AdminPanel';
import TechDatabase from './TechDatabase';
import Reports from './Reports';
import HomeDashboard from './HomeDashboard';
import UserManagementV2 from './UserManagementV2';
import GlobalSearch from './GlobalSearch';
import ActivityLog from './ActivityLog';
import PropertyGroups from './PropertyGroups';
import MobileStackView from './MobileStackView';
import TenantSwitcher from './TenantSwitcher';
import PropertyProfile from './PropertyProfile';
import CorporateStack from './CorporateStack';
import OnboardingWizard from './OnboardingWizard';
import PropertyCompareView from './PropertyCompareView';
import PropertyWizard from './PropertyWizard';
import { DocumentVault, BulkImportWizard, EmbedCodeGenerator } from './AdvancedFeatures';
import { CompetitiveBenchmarks, VendorMarketplace, TemplateLibrary, PropertyDiffView, OnboardingProgress, WebhookSettings, SecurityDashboard } from './GrowthFeatures';
import MobileBottomNav, { MobileMoreMenu } from './MobileBottomNav';
import MobileSupportHub from './MobileSupportHub';
import WalkthroughChecklist from './WalkthroughChecklist';
import FinancialHub from './FinancialHub';
import ToolsHub from './ToolsHub';
import SuperAdminConsole from './SuperAdminConsole';
import {
  MobileStatusDashboard, MobileMyProperties, MobileSupportDial,
  MobileIncidentReporter, MobilePortfolioScorecard, MobileSpendRings,
  MobileIntegrationHealth,
} from './MobileExecutiveViews';
import MobileUserManagement from './MobileUserManagement';
import CommandPalette from './CommandPalette';
import SnapshotCreateDialog from './SnapshotCreateDialog';
import NodeContextMenu from './NodeContextMenu';
import Visualization3D from './Visualization3D';
import CardVisualization from './CardVisualization';
import { FavoritesBar, RecentlyViewed, Breadcrumbs, MobileStackList, useRecentTracker } from './UserFeatures';
import { useRecentViewsApi } from './RecentViewsDropdown';
import { useAnalytics } from './useAnalytics';
import { ApprovalQueue } from './Portals';
import { ReadinessScore } from './MarketplaceTools';
import { AiIntegrationMapper, AiTechAdvisor, AiContractAnalyzer } from './AiFeatures';
import AdminPortal from './AdminPortal';
import AdminConsole from './AdminConsole';
import AiHub from './AiHub';
import TenantFeatureManager from './TenantFeatureManager';
import IntegrationEditorModal from './IntegrationEditorModal';
import FocusModeToggle, { FocusModeExitOverlay } from './common/FocusMode';
import KeyboardShortcutsHelp from './common/KeyboardShortcutsHelp';
import { useTranslation } from '../i18n';
import { requestNotificationPermission, startAlertPolling, stopAlertPolling } from '../notifications';
import { usePermissions } from './usePermissions';
import { Loader2, Cable, X, MousePointer2, Menu, LayoutGrid, Target, GitFork, Circle, ArrowUpDown, AlertTriangle, Lock, Eye, Network as NetworkIcon, Image, FileCode, Globe, RefreshCw, ChevronUp, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Sheet, SheetContent } from './ui/sheet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Base64url-safe JWT payload decode (plain atob breaks on '-'/'_' chars)
const decodeJwt = (t) => JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));

export default function Dashboard({ token: propToken, role, onLogout, onGoToSite, darkMode, onToggleDarkMode }) {
  const { t } = useTranslation();
  const [activeToken, setActiveToken] = useState(propToken);
  const token = activeToken;

  // Ensure original token is always stored for super admin operations
  useEffect(() => {
    if (!localStorage.getItem('lodgiflow_original_token')) {
      localStorage.setItem('lodgiflow_original_token', propToken);
    }
  }, [propToken]);
  const { hasPermission: realHasPermission, hasAny: realHasAny, permissions: realPermissions } = usePermissions(token);

  // Role preview mode
  const [previewRole, setPreviewRole] = useState(null); // { role_id, role_name, permissions: Set }
  const hasPermission = previewRole ? (p) => previewRole.permissions.has(p) : realHasPermission;
  const hasAny = previewRole ? (...ps) => ps.some(p => previewRole.permissions.has(p)) : realHasAny;

  const startRolePreview = async (roleId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/role-preview/${roleId}`, { headers: { Authorization: `Bearer ${token}` } });
      setPreviewRole({ role_id: res.data.role_id, role_name: res.data.role_name, permissions: new Set(res.data.permissions) });
      toast.info(`Previewing as ${res.data.role_name}`, { description: `${res.data.permission_count} permissions active` });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load role'); }
  };
  const exitRolePreview = () => { setPreviewRole(null); toast.success('Exited role preview'); };
  const [stackData, setStackData] = useState(null);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('property') || 'overall';
  });
  const [focusedCategory, setFocusedCategory] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  // D1. Bulk Stack Assign — set of node IDs selected via Shift-click
  const [bulkSelectedNodes, setBulkSelectedNodes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showUsedOnly, setShowUsedOnly] = useState(false);
  const [hideUnconnected, setHideUnconnected] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'home';
  });

  // Sync URL state
  const updateUrl = (tab, property, nodeId) => {
    const params = new URLSearchParams();
    if (tab && tab !== 'home') params.set('tab', tab);
    if (property && property !== 'overall') params.set('property', property);
    if (nodeId) params.set('node', nodeId);
    const qs = params.toString();
    window.history.replaceState({}, '', qs ? `?${qs}` : window.location.pathname);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    updateUrl(tab, selectedProperty, selectedNode?.id);
  };

  // Keep URL query params in sync with (activeTab, selectedProperty, selectedNode) so
  // the "Share this view" button always yields a deep-link that restores the exact state.
  useEffect(() => {
    updateUrl(activeTab, selectedProperty, selectedNode?.id);
  }, [activeTab, selectedProperty, selectedNode]); // eslint-disable-line react-hooks/exhaustive-deps
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Resizable panel widths
  const [leftWidth, setLeftWidth] = useState(280);
  const [rightWidth, setRightWidth] = useState(300);

  // Navigation target for "Manage in Tech Database"
  const [navigateToPlatformId, setNavigateToPlatformId] = useState(null);

  // Global search
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [snapshotDialogOpen, setSnapshotDialogOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [show3D, setShow3D] = useState(false);
  const [vizMenuOpen, setVizMenuOpen] = useState(false);
  const [showCardView, setShowCardView] = useState(false);
  const [lineOpacity, setLineOpacity] = useState(0.8);
  const [lineThickness, setLineThickness] = useState(2);
  const [logoOpacity, setLogoOpacity] = useState(() => parseFloat(localStorage.getItem('lodgiflow_logo_opacity') || '0.3'));
  const [logoStyle, setLogoStyle] = useState(() => localStorage.getItem('lodgiflow_logo_style') || 'watermark');
  const pendingLayoutRef = useRef(null);
  const trackRecent = useRecentTracker(token);
  const { push: pushRecentView } = useRecentViewsApi(token);
  const { trackFeature } = useAnalytics(token, activeTab);

  // Ctrl+K for command palette
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Apply pending layout after GraphCanvas re-mounts from Card View
  useEffect(() => {
    if (!showCardView && pendingLayoutRef.current) {
      const fn = pendingLayoutRef.current;
      pendingLayoutRef.current = null;
      const timer = setTimeout(() => graphRef.current?.[fn]?.(), 600);
      return () => clearTimeout(timer);
    }
  }, [showCardView]);

  // Push notifications: request permission and start polling
  useEffect(() => {
    requestNotificationPermission().then(perm => {
      if (perm === 'granted') {
        startAlertPolling(token, 120000); // Poll every 2 minutes
      }
    });
    return () => stopAlertPolling();
  }, [token]);

  // Breadcrumb state
  const breadcrumbs = useMemo(() => {
    const items = [{ label: 'Home', tab: 'home' }];
    const TAB_LABELS = { visualizer: 'Stack Map', database: 'Tech Database', reports: 'Reports', admin: 'Admin', tools: 'Tools', ai: 'AI', wizard: 'Wizard', financial: 'Financial', 'super-admin': 'Global Admin', 'compare-view': 'Compare', documents: 'Documents', templates: 'Templates', benchmarks: 'Benchmarks', marketplace: 'Marketplace', diff: 'Property Diff', security: 'Security', import: 'Bulk Import', 'support-mobile': 'Support' };
    if (activeTab !== 'home' && TAB_LABELS[activeTab]) {
      items.push({ label: TAB_LABELS[activeTab], tab: activeTab });
    }
    if (activeTab === 'visualizer' && selectedProperty && selectedProperty !== 'overall') {
      const prop = properties.find(p => p.id === selectedProperty);
      if (prop) items.push({ label: prop.name });
    }
    return items;
  }, [activeTab, selectedProperty, properties]);

  // Tenant context — extract from JWT if present
  const tokenTenantId = useMemo(() => {
    try {
      return decodeJwt(token).tenant_id || '';
    } catch { return ''; }
  }, [token]);

  // Track if user is originally a super admin
  const isSuperAdminRef = useRef(null);
  if (isSuperAdminRef.current === null) {
    try {
      const payload = decodeJwt(propToken);
      if (!payload.tenant_id) {
        // Fresh super-admin token — always refresh the stored original token
        isSuperAdminRef.current = true;
        localStorage.setItem('lodgiflow_original_token', propToken);
      } else {
        // Tenant-scoped token — super admin only if a valid super original token is stored
        const origToken = localStorage.getItem('lodgiflow_original_token');
        let origSuper = false;
        if (origToken) {
          try {
            const op = decodeJwt(origToken);
            origSuper = !op.tenant_id && (!op.exp || op.exp * 1000 > Date.now());
          } catch { origSuper = false; }
          if (!origSuper) localStorage.removeItem('lodgiflow_original_token');
        }
        isSuperAdminRef.current = origSuper;
      }
    } catch { isSuperAdminRef.current = false; }
    localStorage.setItem('lodgiflow_is_super_admin', String(isSuperAdminRef.current));
  }
  const isSuperAdmin = isSuperAdminRef.current;

  // Cross-tenant access (for non-super-admin users with multi-tenant visibility)
  const [hasCrossTenantAccess, setHasCrossTenantAccess] = useState(false);
  useEffect(() => {
    if (!isSuperAdmin && token) {
      axios.get(`${BACKEND_URL}/api/my-tenant-access`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setHasCrossTenantAccess(r.data?.is_cross_tenant || false))
        .catch(() => {});
    }
  }, [isSuperAdmin, token]);

  const canSwitchTenants = isSuperAdmin || hasCrossTenantAccess;

  // Fetch tenants for mobile switcher — try stored original token first, fall back on failure
  const [mobileTenants, setMobileTenants] = useState([]);
  useEffect(() => {
    if (!token) return;
    const origToken = localStorage.getItem('lodgiflow_original_token');
    const tokensToTry = [...new Set([origToken, propToken, token].filter(Boolean))];
    (async () => {
      for (const t of tokensToTry) {
        try {
          const payload = decodeJwt(t);
          if (payload.tenant_id) continue; // tenant-scoped token can't list all tenants
          const r = await axios.get(`${BACKEND_URL}/api/tenants`, { headers: { Authorization: `Bearer ${t}` } });
          setMobileTenants(r.data.tenants || r.data || []);
          localStorage.setItem('lodgiflow_original_token', t);
          return;
        } catch {
          if (t === origToken) localStorage.removeItem('lodgiflow_original_token'); // stale/expired
        }
      }
    })();
  }, [token, propToken]); // eslint-disable-line

  const [currentTenantId, setCurrentTenantId] = useState(() => {
    // Tenant-scoped users always use their JWT tenant
    try {
      const payload = decodeJwt(propToken);
      if (payload.tenant_id) return payload.tenant_id;
    } catch { /* ignore */ }
    return localStorage.getItem('lodgiflow_tenant_id') || '';
  });
  const [currentTenantName, setCurrentTenantName] = useState('');
  const [tenantKey, setTenantKey] = useState(0);

  // Detect mobile — width-based, plus touch/PWA devices up to tablet size (iPad portrait = 768)
  const detectMobile = () => {
    const w = window.innerWidth;
    if (w < 768) return true;
    const coarse = window.matchMedia?.('(pointer: coarse)')?.matches;
    const standalone = window.matchMedia?.('(display-mode: standalone)')?.matches;
    return (coarse || standalone) && w < 1024;
  };
  const isMobileRef = useRef(detectMobile());
  const [isMobile, setIsMobile] = useState(detectMobile());

  useEffect(() => {
    const handleResize = () => {
      const mobile = detectMobile();
      setIsMobile(mobile);
      isMobileRef.current = mobile;
      if (mobile) setLeftPanelCollapsed(true);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      // Skip if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;
      if (e.ctrlKey || e.metaKey) return; // Don't intercept Ctrl combos

      if (activeTab === 'visualizer') {
        const layoutKeys = { '1': 'layoutByCategory', '2': 'layoutGrid', '3': 'layoutFlowChart', '4': 'layoutOrgChart', '5': 'layoutPmsHub', '6': 'layoutRadial', '7': 'layoutClean1', '8': 'layoutClean2', '9': 'layoutSwimCat', '0': 'layoutSwimProp', 'l': 'layoutLogoCluster', 's': 'layoutLogoClusterSoft' };
        if (layoutKeys[e.key]) {
          if (showCardView) {
            pendingLayoutRef.current = layoutKeys[e.key];
            setShowCardView(false);
          } else {
            graphRef.current?.[layoutKeys[e.key]]?.();
          }
        }
        else if (e.key === 'h' || e.key === 'H') setHideUnconnected(v => !v);
        else if (e.key === 'f' || e.key === 'F') graphRef.current?.fitToView?.();
        else if (e.key === 's' || e.key === 'S') graphRef.current?.toggleLabelSwap?.();
      }
      if (e.key === 'Escape') { setSelectedNode(null); setSearchOpen(false); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeTab, showCardView]);

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editSourceNode, setEditSourceNode] = useState(null);
  const [integrationModalOpen, setIntegrationModalOpen] = useState(false);
  const [integrationTarget, setIntegrationTarget] = useState(null);
  const [existingIntegration, setExistingIntegration] = useState(null);
  const [saving, setSaving] = useState(false);

  // Quick-add integration from RightPanel
  const [quickAddCallback, setQuickAddCallback] = useState(null);

  const handleQuickAddIntegration = (config) => {
    if (!config) {
      setQuickAddCallback(null);
      return;
    }
    // Store the callback — next node click will call it
    setQuickAddCallback(() => config.onComplete);
  };

  const handleNodeSelect = (node, modifiers = {}) => {
    // If quick-add is active, call the callback instead of selecting
    if (quickAddCallback && node) {
      quickAddCallback(node);
      setQuickAddCallback(null);
      return;
    }
    // D1. Bulk selection: Shift/Meta/Ctrl-click adds/removes the node from the bulk set
    if (node && (modifiers.shiftKey || modifiers.metaKey || modifiers.ctrlKey)) {
      setBulkSelectedNodes(prev => {
        const has = prev.find(n => n.id === node.id);
        return has ? prev.filter(n => n.id !== node.id) : [...prev, node];
      });
      return;
    }
    // Plain click clears any bulk selection
    if (bulkSelectedNodes.length > 0) setBulkSelectedNodes([]);
    setSelectedNode(node);
    if (node) {
      trackRecent('property', node.id, node.name);
      // Push a composite recent-view (property + node) so the user can jump back
      const propName = selectedProperty === 'overall'
        ? 'Overall Map'
        : (properties.find(p => p.id === selectedProperty)?.name || selectedProperty);
      pushRecentView({
        property_id: selectedProperty || 'overall',
        property_name: propName,
        node_id: node.id,
        node_name: node.name || node.short || node.id,
        node_category: node.category || node.category_id || '',
      });
    }
  };

  // Jump from a Recent View entry: switch property first, then let the
  // stackData-load effect restore the node.
  const handleJumpRecentView = useCallback((view) => {
    if (!view) return;
    // Reuse the same "pending node id" hydration flow so restoring after property
    // switch waits for stackData to load
    pendingNodeIdRef.current = view.node_id;
    nodeHydratedRef.current = false;
    if (view.property_id && view.property_id !== selectedProperty) {
      setSelectedProperty(view.property_id);
    } else {
      // Same property — find node directly in current stackData
      const match = stackData?.nodes?.find(n => n.id === view.node_id);
      if (match) setSelectedNode(match);
    }
    setActiveTab('visualizer');
  }, [selectedProperty, stackData]);

  // Layer state
  const [activeLayer, setActiveLayer] = useState('actual');

  // Compare mode state
  const [compareMode, setCompareMode] = useState(false);
  const [compareData, setCompareData] = useState(null);

  // Account status (billing lockout)
  const [accountStatus, setAccountStatus] = useState({ status: 'active', reason: 'ok' });

  // White-label branding
  const [whiteLabel, setWhiteLabel] = useState(null);

  const graphRef = useRef(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Load user viz preferences (logo style/opacity/layout mode/last property/last node) from profile
  const [pendingLayoutMode, setPendingLayoutMode] = useState(null);
  const layoutAppliedRef = useRef(false);
  // Track whether we've already hydrated the last-used property (so a user's manual pick
  // during the fetch race isn't silently overwritten)
  const propertyHydratedRef = useRef(false);
  // Seed the pending node id from the URL immediately so a deep-link doesn't get
  // stomped by the async viz-preferences fetch below.
  const pendingNodeIdRef = useRef(new URLSearchParams(window.location.search).get('node') || null);
  const nodeHydratedRef = useRef(false);
  useEffect(() => {
    if (!token) return;
    axios.get(`${BACKEND_URL}/api/user/viz-preferences`, { headers })
      .then(r => {
        const p = r.data || {};
        if (p.logo_style) {
          setLogoStyle(p.logo_style);
          localStorage.setItem('lodgiflow_logo_style', p.logo_style);
          graphRef.current?.setLogoStyle?.(p.logo_style);
        }
        if (typeof p.logo_opacity === 'number') {
          setLogoOpacity(p.logo_opacity);
          localStorage.setItem('lodgiflow_logo_opacity', String(p.logo_opacity));
          graphRef.current?.setLogoOpacity?.(p.logo_opacity);
        }
        if (p.layout_mode) {
          localStorage.setItem('lodgiflow_layout_mode', p.layout_mode);
          setPendingLayoutMode(p.layout_mode);
        }
        // Hydrate last-used property filter. URL param wins (so shared links work),
        // otherwise fall back to the stored value.
        const urlProperty = new URLSearchParams(window.location.search).get('property');
        if (!urlProperty && p.last_property_id && !propertyHydratedRef.current) {
          propertyHydratedRef.current = true;
          setSelectedProperty(p.last_property_id);
        } else {
          propertyHydratedRef.current = true;
        }
        // Stash the last-selected node id — reconciled with stackData once it arrives.
        // Skip if we already have one from the URL (deep-link wins).
        if (!pendingNodeIdRef.current && p.last_node_id) pendingNodeIdRef.current = p.last_node_id;
      }).catch(() => { propertyHydratedRef.current = true; });
  }, [token, headers]);

  // Persist selectedProperty changes to profile (debounced via saveVizPrefs)
  useEffect(() => {
    if (!token) return;
    if (!propertyHydratedRef.current) return; // avoid saving before we've hydrated
    saveVizPrefs({ last_property_id: selectedProperty || 'overall' }, true);
  }, [selectedProperty, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist selectedNode changes (id only) to profile
  useEffect(() => {
    if (!token) return;
    if (!nodeHydratedRef.current) return; // wait for initial hydration attempt
    saveVizPrefs({ last_node_id: selectedNode?.id || null }, true);
  }, [selectedNode, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // When stackData loads (or after property switch), try to restore the last-selected
  // node. Only fires once per pending id — a subsequent property switch shouldn't
  // resurrect an old selection.
  useEffect(() => {
    if (!stackData || !stackData.nodes) return;
    const wantId = pendingNodeIdRef.current;
    if (!wantId) { nodeHydratedRef.current = true; return; }
    const match = stackData.nodes.find(n => n.id === wantId);
    if (match) setSelectedNode(match);
    // Mark hydration complete either way so save-on-change kicks in
    pendingNodeIdRef.current = null;
    nodeHydratedRef.current = true;
  }, [stackData]);

  // Apply the pending layout mode when Stack Map becomes active AND both the graph
  // and its data are ready. This handles the common case where viz-prefs fetch
  // resolves after GraphCanvas has mounted but before it has drawn the simulation.
  useEffect(() => {
    if (activeTab !== 'visualizer') return;
    if (!pendingLayoutMode || layoutAppliedRef.current) return;
    if (!stackData) return;
    const t = setTimeout(() => {
      if (graphRef.current?.applyStoredLayout) {
        graphRef.current.applyStoredLayout(pendingLayoutMode);
        layoutAppliedRef.current = true;
      }
    }, 900);
    return () => clearTimeout(t);
  }, [activeTab, showCardView, stackData, pendingLayoutMode]);

  const vizPrefsTimer = useRef(null);
  const vizPrefsPending = useRef({});
  const saveVizPrefs = useCallback((patch, debounce = false) => {
    const doSave = (body) => axios.put(`${BACKEND_URL}/api/user/viz-preferences`, body, { headers }).catch(() => {});
    if (debounce) {
      // Merge with any pending patch so concurrent debounced calls don't clobber each other
      vizPrefsPending.current = { ...vizPrefsPending.current, ...patch };
      clearTimeout(vizPrefsTimer.current);
      vizPrefsTimer.current = setTimeout(() => {
        const body = vizPrefsPending.current;
        vizPrefsPending.current = {};
        doSave(body);
      }, 600);
    } else doSave(patch);
  }, [headers]);

  // Fetch account status for billing lockout
  useEffect(() => {
    const fetchAccountStatus = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/billing/v2/account-status`, { headers });
        setAccountStatus(res.data);
      } catch { /* default active */ }
    };
    fetchAccountStatus();
  }, [headers, tenantKey]);

  // Fetch white-label config
  useEffect(() => {
    const fetchWhiteLabel = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/admin/whitelabel`, { headers });
        if (res.data?.configured) {
          setWhiteLabel(res.data);
          // Apply CSS variables
          const root = document.documentElement;
          if (res.data.primary_color) root.style.setProperty('--wl-primary', res.data.primary_color);
          if (res.data.accent_color) root.style.setProperty('--wl-accent', res.data.accent_color);
          if (res.data.header_bg) root.style.setProperty('--wl-header-bg', res.data.header_bg);
          if (res.data.favicon_url) {
            const link = document.querySelector("link[rel~='icon']");
            if (link) link.href = res.data.favicon_url;
          }
          if (res.data.platform_name) document.title = res.data.platform_name;
        }
      } catch { /* not configured, use defaults */ }
    };
    fetchWhiteLabel();
    return () => {
      // Cleanup CSS variables on unmount
      const root = document.documentElement;
      root.style.removeProperty('--wl-primary');
      root.style.removeProperty('--wl-accent');
      root.style.removeProperty('--wl-header-bg');
    };
  }, [headers, tenantKey]);

  const isReadOnly = accountStatus.status === 'read_only' || accountStatus.status === 'disabled';

  const fetchProperties = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/properties`, { headers });
      setProperties(res.data);
    } catch (err) {
      console.error('Failed to fetch properties:', err);
    }
  }, [headers]);

  const fetchStack = useCallback(async (propertyId) => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (propertyId && propertyId !== 'overall') params.property_id = propertyId;
      if (activeLayer) params.layer = activeLayer;
      const res = await axios.get(`${BACKEND_URL}/api/stack`, { headers, params });
      setStackData(res.data);
    } catch (err) {
      setError('Failed to load tech stack data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [headers, activeLayer]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties, tenantKey]);

  useEffect(() => {
    fetchStack(selectedProperty);
  }, [selectedProperty, fetchStack, tenantKey]);

  // Fetch compare data when compare mode enabled
  useEffect(() => {
    if (!compareMode || !selectedProperty || selectedProperty === 'overall') {
      setCompareData(null);
      return;
    }
    const fetchCompare = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/stack/compare`, {
          headers, params: { property_id: selectedProperty },
        });
        setCompareData(res.data);
      } catch {
        setCompareData(null);
      }
    };
    fetchCompare();
  }, [compareMode, selectedProperty, headers]);

  const handlePropertyChange = (propId) => {
    setSelectedProperty(propId);
    setSelectedNode(null);
    setFocusedCategory(null);
    setSearchQuery('');
    // Default to hiding unused platforms when viewing a specific property
    setShowUsedOnly(propId !== 'overall');
  };

  const handleCategoryFocus = (catId) => {
    setFocusedCategory(prev => prev === catId ? null : catId);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleFitToView = () => {
    if (graphRef.current?.fitToView) {
      graphRef.current.fitToView();
    }
  };

  const handleResetView = () => {
    setFocusedCategory(null);
    setSelectedNode(null);
    setSearchQuery('');
    setShowUsedOnly(false);
    if (graphRef.current?.resetView) {
      graphRef.current.resetView();
    }
  };

  // --- Edit Mode Handlers ---
  const toggleEditMode = () => {
    if (editMode) {
      // Exiting edit mode
      setEditMode(false);
      setEditSourceNode(null);
      setIntegrationTarget(null);
      setIntegrationModalOpen(false);
    } else {
      // Entering edit mode
      setEditMode(true);
      setSelectedNode(null);
    }
  };

  // Called from GraphCanvas when a node is clicked in edit mode
  const handleEditNodeClick = (node) => {
    if (!editSourceNode) {
      // First click — select source
      setEditSourceNode(node);
      setSelectedNode(node); // Also show in RightPanel
    } else if (node.id === editSourceNode.id) {
      // Same node — deselect
      setEditSourceNode(null);
    } else {
      // Second click — select target, check for existing integration
      setIntegrationTarget(node);

      // Check if an integration already exists between these nodes
      const links = stackData?.links || [];
      const existing = links.find(l => {
        const sid = l.source?.id || l.source;
        const tid = l.target?.id || l.target;
        return (sid === editSourceNode.id && tid === node.id) ||
               (sid === node.id && tid === editSourceNode.id);
      });

      setExistingIntegration(existing || null);
      setIntegrationModalOpen(true);
    }
  };

  // Called when clicking empty space on canvas in edit mode
  const handleEditCanvasClick = () => {
    setEditSourceNode(null);
  };

  // Save integration
  const handleSaveIntegration = async (integrationData) => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/integrations`, integrationData, { headers });
      toast.success('Integration saved', {
        description: `${integrationData.direction === 'two-way' ? 'Two-way' : 'One-way'} connection created.`,
      });
      // Refresh data
      await fetchStack(selectedProperty);
      setIntegrationModalOpen(false);
      setEditSourceNode(null);
      setIntegrationTarget(null);
      setExistingIntegration(null);
    } catch (err) {
      console.error('Failed to save integration:', err);
      toast.error('Failed to save integration', {
        description: err.response?.data?.detail || 'An error occurred.',
      });
    } finally {
      setSaving(false);
    }
  };

  // Delete integration
  const handleDeleteIntegration = async (integrationData) => {
    setSaving(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/integrations`, {
        headers,
        params: {
          from_platform_id: integrationData.from_platform_id,
          to_platform_id: integrationData.to_platform_id,
        },
      });
      toast.success('Integration deleted');
      await fetchStack(selectedProperty);
      setIntegrationModalOpen(false);
      setEditSourceNode(null);
      setIntegrationTarget(null);
      setExistingIntegration(null);
    } catch (err) {
      console.error('Failed to delete integration:', err);
      toast.error('Failed to delete integration', {
        description: err.response?.data?.detail || 'An error occurred.',
      });
    } finally {
      setSaving(false);
    }
  };

  // Delete integration from RightPanel
  const handleDeleteIntegrationFromPanel = async (fromId, toId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/integrations`, {
        headers,
        params: { from_platform_id: fromId, to_platform_id: toId },
      });
      toast.success('Integration deleted');
      await fetchStack(selectedProperty);
    } catch (err) {
      console.error('Failed to delete integration:', err);
      toast.error('Failed to delete integration');
    }
  };

  if (showAdmin && role === 'admin') {
    return (
      <AdminPanel
        token={token}
        onBack={() => { setShowAdmin(false); fetchStack(selectedProperty); }}
        onLogout={onLogout}
      />
    );
  }

  return (
    <div className="h-screen w-screen flex flex-col overflow-hidden bg-background">
      <FocusModeExitOverlay />
      <Header
        properties={properties}
        selectedProperty={selectedProperty}
        onPropertyChange={handlePropertyChange}
        searchQuery={searchQuery}
        onSearch={handleSearch}
        onFitToView={handleFitToView}
        role={role}
        onAdmin={() => setShowAdmin(true)}
        onLogout={onLogout}
        stackData={stackData}
        graphRef={graphRef}
        selectedNode={selectedNode}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        editMode={editMode}
        onToggleEditMode={toggleEditMode}
        activeLayer={activeLayer}
        onLayerChange={setActiveLayer}
        isMobile={isMobile}
        compareMode={compareMode}
        onToggleCompare={() => setCompareMode(v => !v)}
        onOpenSearch={() => setSearchOpen(true)}
        darkMode={darkMode}
        onToggleDarkMode={onToggleDarkMode}
        onGoToSite={onGoToSite}
        hasPermission={hasPermission}
        whiteLabel={whiteLabel}
        isSuperAdmin={isSuperAdmin}
        token={token}
        onJumpRecentView={handleJumpRecentView}
        onCreateSnapshot={() => setSnapshotDialogOpen(true)}
        tenantSwitcher={role === 'admin' && canSwitchTenants && (
          <TenantSwitcher
            token={localStorage.getItem('lodgiflow_original_token') || token}
            currentTenantId={currentTenantId}
            onSwitch={(newToken, tenantId, tenantName) => {
              localStorage.setItem('lodgiflow_token', newToken);
              setActiveToken(newToken);
              setCurrentTenantId(tenantId);
              setCurrentTenantName(tenantName);
              setSelectedProperty('overall');
              setTenantKey(k => k + 1);
            }}
          />
        )}
      />

      {/* Billing lockout banner */}
      {isReadOnly && (
        <div className="shrink-0 px-4 py-2 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800 flex items-center gap-3" data-testid="billing-lockout-banner">
          <Lock className="h-4 w-4 text-amber-600 shrink-0" />
          <p className="text-xs text-amber-800 dark:text-amber-200 flex-1">{accountStatus.message || 'Your account is in read-only mode. Editing features are disabled.'}</p>
          <Button size="sm" variant="outline" className="shrink-0 text-xs border-amber-300 text-amber-700 hover:bg-amber-100" onClick={() => handleTabChange('admin')} data-testid="billing-upgrade-btn">
            {accountStatus.status === 'disabled' ? 'Reactivate' : 'Upgrade'}
          </Button>
        </div>
      )}

      {/* Role preview banner */}
      {previewRole && (
        <div className="shrink-0 px-4 py-2 bg-violet-50 dark:bg-violet-950/30 border-b border-violet-200 dark:border-violet-800 flex items-center gap-3" data-testid="role-preview-banner">
          <Eye className="h-4 w-4 text-violet-600 shrink-0" />
          <p className="text-xs text-violet-800 dark:text-violet-200 flex-1">
            {t('admin.previewingAs')} <strong>{previewRole.role_name}</strong> — {previewRole.permissions.size} {t('admin.permissionsActive')}. 
          </p>
          <Button size="sm" variant="outline" className="shrink-0 text-xs border-violet-300 text-violet-700 hover:bg-violet-100" onClick={exitRolePreview} data-testid="exit-role-preview-btn">
            {t('admin.exitPreview')}
          </Button>
        </div>
      )}

      {/* Mobile tenant selector bar */}
      {isMobile && isSuperAdmin && (
        <div className="shrink-0 px-3 py-2 border-b bg-purple-50 dark:bg-purple-950/20 flex items-center gap-2" data-testid="mobile-tenant-bar">
          <div className="w-2 h-2 rounded-full bg-purple-500 shrink-0" />
          <span className="text-xs text-purple-700 font-semibold shrink-0">Tenant:</span>
          <select
            value={currentTenantId || 'all'}
            onChange={async (e) => {
              const val = e.target.value;
              try {
                const origToken = localStorage.getItem('lodgiflow_original_token') || token;
                if (val === 'all') {
                  localStorage.setItem('lodgiflow_token', origToken);
                  setActiveToken(origToken);
                  setCurrentTenantId('');
                  setCurrentTenantName('All Tenants');
                } else {
                  const res = await axios.post(`${BACKEND_URL}/api/admin/tenants/${val}/switch`, {}, {
                    headers: { Authorization: `Bearer ${origToken}` },
                  });
                  const newToken = res.data.token;
                  localStorage.setItem('lodgiflow_token', newToken);
                  setActiveToken(newToken);
                  setCurrentTenantId(val);
                  setCurrentTenantName(res.data.tenant_name || val);
                }
                setSelectedProperty('overall');
                setTenantKey(k => k + 1);
              } catch { toast.error('Failed to switch tenant'); }
            }}
            className="flex-1 h-9 rounded-lg border-2 border-purple-200 bg-white dark:bg-background px-3 text-sm font-medium min-w-0"
            data-testid="mobile-tenant-select"
          >
            <option value="all">All Tenants</option>
            {(mobileTenants || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
      )}

      {/* Breadcrumbs */}
      <Breadcrumbs items={breadcrumbs} onNavigate={(item) => item.tab && handleTabChange(item.tab)} />

      {/* Favorites bar */}
      <FavoritesBar token={token} onNavigate={(fav) => {
        if (fav.type === 'property') { setSelectedProperty(fav.id); handleTabChange('visualizer'); }
        else if (fav.type === 'report') handleTabChange('reports');
        else if (fav.type === 'product' || fav.type === 'vendor') handleTabChange('database');
      }} />

      {activeTab === 'home' ? (
        <HomeDashboard
          key={`home-${tenantKey}`}
          token={token}
          role={role}
          permissions={previewRole ? Array.from(previewRole.permissions || []) : Array.from(realPermissions || [])}
          isMobile={isMobile}
          onNavigate={setActiveTab}
          onJumpProperty={(propId) => { setSelectedProperty(propId); handleTabChange('visualizer'); }}
        />
      ) : activeTab === 'visualizer' ? (
        isMobile ? (
          /* Mobile: Card View with property selector */
          <div key={`viz-mobile-${tenantKey}`} className="flex flex-col flex-1 overflow-hidden pb-mobile-nav">
            {/* Property selector */}
            <div className="shrink-0 px-3 py-2 border-b bg-card/50">
              <select
                value={selectedProperty}
                onChange={e => { setSelectedProperty(e.target.value); fetchStack(e.target.value); trackRecent('property', e.target.value, properties.find(p => p.id === e.target.value)?.name || e.target.value); }}
                className="w-full h-10 rounded-lg border bg-background px-3 text-sm"
              >
                <option value="overall">All Platforms (Overview)</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            {loading ? (
              <div className="flex-1 flex items-center justify-center"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>
            ) : (
              <CardVisualization stackData={stackData} selectedProperty={selectedProperty} onSelectNode={setSelectedNode} hideUnconnected={hideUnconnected} />
            )}
          </div>
        ) : (
        <div key={`viz-${tenantKey}`} className="flex flex-1 overflow-hidden relative" style={{ maxWidth: '100%' }}>
          {/* Left Panel - hidden on mobile, shown in drawer */}
          {!isMobile && (
            <>
            <LeftPanel
              categories={stackData?.categories || []}
              focusedCategory={focusedCategory}
              onCategoryFocus={handleCategoryFocus}
              showUsedOnly={showUsedOnly}
              onShowUsedOnly={setShowUsedOnly}
              onResetView={handleResetView}
              onFitToView={handleFitToView}
              stackData={stackData}
              selectedProperty={selectedProperty}
              collapsed={leftPanelCollapsed}
              onToggleCollapse={() => setLeftPanelCollapsed(v => !v)}
              token={token}
              width={leftWidth}
              onEditProfile={() => setActiveTab('property-profile')}
            />
            {!leftPanelCollapsed && (
              <ResizeHandle onResize={(delta) => setLeftWidth(w => Math.max(200, Math.min(Math.floor(window.innerWidth * 0.3), w + delta)))} />
            )}
            </>
          )}

          {/* Mobile left panel drawer */}
          {isMobile && (
            <Sheet open={mobileDrawerOpen} onOpenChange={setMobileDrawerOpen}>
              <SheetContent side="left" className="w-[280px] p-0">
                <LeftPanel
                  categories={stackData?.categories || []}
                  focusedCategory={focusedCategory}
                  onCategoryFocus={(catId) => { handleCategoryFocus(catId); setMobileDrawerOpen(false); }}
                  showUsedOnly={showUsedOnly}
                  onShowUsedOnly={setShowUsedOnly}
                  onResetView={() => { handleResetView(); setMobileDrawerOpen(false); }}
                  onFitToView={handleFitToView}
                  stackData={stackData}
                  selectedProperty={selectedProperty}
                  collapsed={false}
                  onToggleCollapse={() => setMobileDrawerOpen(false)}
                  isMobileDrawer
                  token={token}
                />
              </SheetContent>
            </Sheet>
          )}

          {/* Graph Canvas */}
          <div className="flex-1 relative min-w-0 overflow-hidden" onContextMenu={(e) => {
            // Find if the click is on a D3 node
            const target = e.target;
            const nodeEl = target.closest?.('circle, rect, text');
            if (nodeEl && selectedNode) {
              e.preventDefault();
              setContextMenu({ x: e.clientX, y: e.clientY, node: selectedNode });
            }
          }}>
            {/* Mobile: filter button */}
            {isMobile && (
              <Button
                variant="outline"
                size="icon"
                className="absolute top-3 left-3 z-20 h-9 w-9 bg-card/90 backdrop-blur shadow-md"
                onClick={() => setMobileDrawerOpen(true)}
                data-testid="mobile-filter-button"
              >
                <Menu className="h-4 w-4" />
              </Button>
            )}

            {/* Edit Mode Banner */}
            {editMode && (
              <div
                className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3 px-4 py-2.5 rounded-xl border bg-card/95 backdrop-blur shadow-lg"
                data-testid="edit-mode-banner"
              >
                <Cable className="h-4 w-4 text-primary shrink-0" />
                <div className="text-xs">
                  {!editSourceNode ? (
                    <span className="text-muted-foreground">
                      <strong className="text-foreground">Step 1:</strong> Click a <strong className="text-foreground">source</strong> platform node
                    </span>
                  ) : (
                    <span className="text-muted-foreground">
                      <strong className="text-foreground">Step 2:</strong> Click a <strong className="text-foreground">target</strong> platform to connect with{' '}
                      <Badge
                        variant="outline"
                        className="text-[10px] px-1.5 py-0 h-4 inline-flex mx-0.5"
                        style={{
                          borderColor: `${editSourceNode.category_color}60`,
                          color: editSourceNode.category_color,
                        }}
                      >
                        {editSourceNode.short}
                      </Badge>
                    </span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 ml-1 shrink-0"
                  onClick={toggleEditMode}
                  data-testid="edit-mode-close"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}

            {/* Compare Mode Legend */}
            {compareMode && compareData && (
              <div
                className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-4 px-4 py-2.5 rounded-xl border bg-card/95 backdrop-blur shadow-lg"
                data-testid="compare-legend"
              >
                <span className="text-xs font-semibold text-foreground">Actual vs Ideal</span>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-emerald-500 border-2 border-emerald-600" />
                  <span className="text-[10px] text-muted-foreground">Both ({compareData.stats?.matches || 0})</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-amber-400 border-2 border-amber-500" />
                  <span className="text-[10px] text-muted-foreground">Actual only ({compareData.stats?.actual_only || 0})</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full border-2 border-blue-500 border-dashed bg-blue-100" />
                  <span className="text-[10px] text-muted-foreground">Ideal only ({compareData.stats?.ideal_only || 0})</span>
                </div>
              </div>
            )}

            {loading ? (
              <div className="absolute inset-0 flex items-center justify-center bg-[#F7FAFC]">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Loading tech stack...</span>
                </div>
              </div>
            ) : error ? (
              <div className="absolute inset-0 flex items-center justify-center bg-[#F7FAFC]">
                <p className="text-destructive text-sm">{error}</p>
              </div>
            ) : stackData ? (
              isMobile ? (
                <CardVisualization
                  stackData={stackData}
                  selectedProperty={selectedProperty}
                  onSelectNode={handleNodeSelect}
                  hideUnconnected={hideUnconnected}
                />
              ) : (
              showCardView ? (
                <CardVisualization stackData={stackData} selectedProperty={selectedProperty} onSelectNode={setSelectedNode} onClose={() => setShowCardView(false)} hideUnconnected={hideUnconnected} />
              ) : (
                <GraphCanvas
                  ref={graphRef}
                  data={stackData}
                  focusedCategory={focusedCategory}
                  selectedNode={editMode ? null : selectedNode}
                  bulkSelectedIds={bulkSelectedNodes.map(n => n.id)}
                  onNodeSelect={editMode ? handleEditNodeClick : handleNodeSelect}
                  onCanvasClick={editMode ? handleEditCanvasClick : undefined}
                  searchQuery={searchQuery}
                  showUsedOnly={showUsedOnly}
                  hideUnconnected={hideUnconnected}
                  selectedProperty={selectedProperty}
                  editMode={editMode}
                  editSourceNode={editSourceNode}
                  compareMode={compareMode}
                  compareData={compareData}
                  onLayoutChange={(mode) => saveVizPrefs({ layout_mode: mode }, true)}
                />
              )
              )
            ) : null}

            {/* D1. Bulk Stack Assign toolbar */}
            {!isMobile && !editMode && selectedProperty && selectedProperty !== 'overall' && (
              <BulkStackToolbar
                selectedNodes={bulkSelectedNodes}
                selectedProperty={selectedProperty}
                properties={properties}
                token={token}
                onDeselect={() => setBulkSelectedNodes([])}
                onDone={() => { setBulkSelectedNodes([]); fetchStack(selectedProperty); }}
              />
            )}

            {/* Visualization Controls */}
            {!isMobile && stackData && (
              <div className="absolute bottom-3 left-3 z-20 flex items-end gap-2" data-testid="layout-buttons">
                {/* Refresh button */}
                <Button variant="outline" size="sm" className="h-8 w-8 p-0 bg-card/90 backdrop-blur shadow-md" data-testid="refresh-stack"
                  onClick={() => fetchStack(selectedProperty)}>
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>

                {/* A4. Focus mode toggle — hides chrome, keyboard shortcut Shift+F */}
                <FocusModeToggle className="bg-card/90 backdrop-blur shadow-md" />

                {/* Viz options menu */}
                <div className="relative">
                  <Button variant="outline" size="sm" className="h-8 gap-1.5 bg-card/90 backdrop-blur shadow-md px-3 text-xs"
                    onClick={() => setVizMenuOpen(v => !v)} data-testid="viz-menu-trigger">
                    <Menu className="h-3.5 w-3.5" /> Options {vizMenuOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronUp className="h-3 w-3 rotate-180" />}
                  </Button>

                  {vizMenuOpen && (
                    <div className="absolute bottom-full left-0 mb-2 w-56 bg-card/95 backdrop-blur-xl border rounded-xl shadow-2xl p-2 space-y-0.5 animate-in fade-in slide-in-from-bottom-2 duration-150 max-h-[70vh] overflow-y-auto" data-testid="viz-menu-dropdown">
                      <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold px-2 pt-1 pb-0.5">Layouts</p>
                      {[
                        { fn: 'layoutByCategory', icon: Circle, label: t('stackMap.force'), key: '1' },
                        { fn: 'layoutLogoCluster', icon: Circle, label: 'Logo Cluster', key: 'L' },
                        { fn: 'layoutLogoClusterSoft', icon: Circle, label: 'Logo Cluster (Soft)', key: 'S' },
                        { fn: 'layoutGrid', icon: LayoutGrid, label: t('stackMap.structured'), key: '2' },
                        { fn: 'layoutFlowChart', icon: GitFork, label: t('stackMap.flowchart'), key: '3' },
                        { fn: 'layoutOrgChart', icon: NetworkIcon, label: t('stackMap.orgChart'), key: '4' },
                        { fn: 'layoutPmsHub', icon: Target, label: t('stackMap.pmsHub'), key: '5' },
                        { fn: 'layoutRadial', icon: Circle, label: t('stackMap.radial'), key: '6' },
                        { fn: 'layoutClean1', icon: Circle, label: 'Clean 1', key: '7' },
                        { fn: 'layoutClean2', icon: LayoutGrid, label: 'Clean 2', key: '8' },
                        { fn: 'layoutSwimCat', icon: GitFork, label: 'Swimlane: Categories', key: '9' },
                        { fn: 'layoutSwimProp', icon: GitFork, label: 'Swimlane: Deployed', key: '0' },
                      ].map(l => {
                        const Icon = l.icon;
                        return (
                          <button key={l.fn} onClick={() => {
                            if (showCardView) {
                              pendingLayoutRef.current = l.fn;
                              setShowCardView(false);
                            } else {
                              graphRef.current?.[l.fn]?.();
                            }
                            setVizMenuOpen(false);
                          }}
                            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs hover:bg-muted/60 transition-colors">
                            <Icon className="h-3.5 w-3.5 text-muted-foreground" /> <span className="flex-1 text-left">{l.label}</span> <kbd className="text-[9px] text-muted-foreground/50 bg-muted px-1 rounded">{l.key}</kbd>
                          </button>
                        );
                      })}
                      <div className="border-t my-1" />
                      <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold px-2 pt-1 pb-0.5">Display</p>
                      <button onClick={() => setHideUnconnected(v => !v)} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs hover:bg-muted/60 transition-colors">
                        <Eye className="h-3.5 w-3.5 text-muted-foreground" /> <span className="flex-1 text-left">{hideUnconnected ? t('stackMap.showingConnected') : t('stackMap.hideUnconnected')}</span> <kbd className="text-[9px] text-muted-foreground/50 bg-muted px-1 rounded">H</kbd>
                      </button>
                      <button onClick={() => { graphRef.current?.toggleLabelSwap?.(); setVizMenuOpen(false); }} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs hover:bg-muted/60 transition-colors">
                        <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" /> <span className="flex-1 text-left">{t('stackMap.swapLabels')}</span> <kbd className="text-[9px] text-muted-foreground/50 bg-muted px-1 rounded">S</kbd>
                      </button>
                      <button onClick={() => { graphRef.current?.fitToView?.(); setVizMenuOpen(false); }} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs hover:bg-muted/60 transition-colors">
                        <Maximize2 className="h-3.5 w-3.5 text-muted-foreground" /> <span className="flex-1 text-left">Fit to View</span> <kbd className="text-[9px] text-muted-foreground/50 bg-muted px-1 rounded">F</kbd>
                      </button>
                      <div className="border-t my-1" />
                      <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold px-2 pt-1 pb-0.5">Export</p>
                      <button onClick={() => { graphRef.current?.exportPng?.(); setVizMenuOpen(false); }} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs hover:bg-muted/60 transition-colors">
                        <Image className="h-3.5 w-3.5 text-muted-foreground" /> <span className="flex-1 text-left">Download PNG</span>
                      </button>
                      <button onClick={() => { graphRef.current?.exportSvg?.(); setVizMenuOpen(false); }} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs hover:bg-muted/60 transition-colors">
                        <FileCode className="h-3.5 w-3.5 text-muted-foreground" /> <span className="flex-1 text-left">Download SVG</span>
                      </button>
                      <div className="border-t my-1" />
                      {/* Line controls */}
                      <div className="px-2 py-1.5 space-y-2">
                        <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Lines</p>
                        <div>
                          <div className="flex justify-between text-[9px] text-muted-foreground mb-0.5">
                            <span>Opacity</span>
                            <span>{Math.round(lineOpacity * 100)}%</span>
                          </div>
                          <input type="range" min="0.1" max="1" step="0.05"
                            value={lineOpacity}
                            onChange={e => { const v = parseFloat(e.target.value); setLineOpacity(v); graphRef.current?.setLineOpacity?.(v); }}
                            className="w-full h-1 accent-primary" style={{ height: '4px' }} />
                        </div>
                        <div>
                          <div className="flex justify-between text-[9px] text-muted-foreground mb-0.5">
                            <span>Thickness</span>
                            <span>{lineThickness.toFixed(1)}</span>
                          </div>
                          <input type="range" min="0.5" max="6" step="0.5"
                            value={lineThickness}
                            onChange={e => { const v = parseFloat(e.target.value); setLineThickness(v); graphRef.current?.setLineThickness?.(v); }}
                            className="w-full h-1 accent-primary" style={{ height: '4px' }} />
                        </div>
                      </div>
                      <div className="border-t my-1" />
                      {/* Vendor logo controls */}
                      <div className="px-2 py-1.5 space-y-2">
                        <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Vendor Logos</p>
                        <div className="flex gap-1" data-testid="logo-style-toggle">
                          {['watermark', 'badge'].map(s => (
                            <button key={s}
                              onClick={() => { setLogoStyle(s); graphRef.current?.setLogoStyle?.(s); saveVizPrefs({ logo_style: s }); }}
                              className={`flex-1 px-2 py-1 rounded-md text-[10px] font-medium capitalize transition-colors ${
                                logoStyle === s ? 'bg-primary text-primary-foreground' : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                              }`}
                              data-testid={`logo-style-${s}`}>
                              {s}
                            </button>
                          ))}
                        </div>
                        <div>
                          <div className="flex justify-between text-[9px] text-muted-foreground mb-0.5">
                            <span>Logo Opacity</span>
                            <span>{Math.round(logoOpacity * 100)}%</span>
                          </div>
                          <input type="range" min="0" max="1" step="0.05"
                            value={logoOpacity}
                            onChange={e => { const v = parseFloat(e.target.value); setLogoOpacity(v); graphRef.current?.setLogoOpacity?.(v); saveVizPrefs({ logo_opacity: v }, true); }}
                            className="w-full h-1 accent-primary" style={{ height: '4px' }} data-testid="logo-opacity-slider" />
                        </div>
                      </div>
                      <div className="border-t my-1" />
                      <button onClick={() => { setShowCardView(true); setVizMenuOpen(false); }} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs hover:bg-muted/60 transition-colors text-emerald-600">
                        <LayoutGrid className="h-3.5 w-3.5" /> <span className="flex-1 text-left font-medium">Card View</span>
                      </button>
                      <button onClick={() => { setShow3D(true); setVizMenuOpen(false); }} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs hover:bg-muted/60 transition-colors text-blue-600">
                        <Globe className="h-3.5 w-3.5" /> <span className="flex-1 text-left font-medium">3D View</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - Sheet on mobile, collapsible sidebar on desktop */}
          {!isMobile && selectedNode && !rightPanelCollapsed && (
            <>
            <ResizeHandle onResize={(delta) => setRightWidth(w => Math.max(250, Math.min(Math.floor(window.innerWidth * 0.35), w - delta)))} />
            <RightPanel
              selectedNode={selectedNode}
              stackData={stackData}
              selectedProperty={selectedProperty}
              onClose={() => setSelectedNode(null)}
              role={role}
              onDeleteIntegration={handleDeleteIntegrationFromPanel}
              onNavigateToDatabase={(platformId) => {
                setSelectedNode(null);
                setNavigateToPlatformId(platformId);
                setActiveTab('database');
              }}
              activeLayer={activeLayer}
              token={token}
              onRefreshStack={() => fetchStack(selectedProperty)}
              width={rightWidth}
              onQuickAddIntegration={handleQuickAddIntegration}
            />
            </>
          )}

          {/* Edge tab buttons for collapsing panels */}
          {!isMobile && (
            <>
              {/* Left panel edge tab */}
              <button
                onClick={() => setLeftPanelCollapsed(v => !v)}
                className="absolute top-3 z-40 flex items-center gap-1 bg-card border shadow-lg rounded-r-lg pl-1 pr-2 py-1.5 hover:bg-muted/80 transition-all duration-200 group"
                style={{ left: leftPanelCollapsed ? '42px' : `${leftWidth || 280}px`, transition: 'left 0.2s ease' }}
                data-testid="left-panel-edge-tab"
                title={leftPanelCollapsed ? 'Show controls' : 'Hide controls'}
              >
                {leftPanelCollapsed
                  ? <ChevronRight className="h-4 w-4 text-primary" />
                  : <ChevronLeft className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                }
                <span className="text-[9px] font-medium text-muted-foreground group-hover:text-foreground">{leftPanelCollapsed ? 'Controls' : ''}</span>
              </button>
              {/* Right panel edge tab (always visible when node selected) */}
              {selectedNode && (
                <button
                  onClick={() => setRightPanelCollapsed(v => !v)}
                  className="absolute top-3 z-40 flex items-center gap-1 bg-card border shadow-lg rounded-l-lg pr-1 pl-2 py-1.5 hover:bg-muted/80 transition-all duration-200 group"
                  style={{ right: rightPanelCollapsed ? '0px' : `${rightWidth || 300}px`, transition: 'right 0.2s ease' }}
                  data-testid="right-panel-edge-tab"
                  title={rightPanelCollapsed ? 'Show details' : 'Hide details'}
                >
                  <span className="text-[9px] font-medium text-muted-foreground group-hover:text-foreground">{rightPanelCollapsed ? 'Details' : ''}</span>
                  {rightPanelCollapsed
                    ? <ChevronLeft className="h-4 w-4 text-primary" />
                    : <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                  }
                </button>
              )}
            </>
          )}
          {isMobile && !editMode && selectedNode && (
            <Sheet open={!!selectedNode} onOpenChange={(open) => { if (!open) setSelectedNode(null); }}>
              <SheetContent side="bottom" className="h-[70vh] p-0 rounded-t-2xl">
                <RightPanel
                  selectedNode={selectedNode}
                  stackData={stackData}
                  selectedProperty={selectedProperty}
                  onClose={() => setSelectedNode(null)}
                  role={role}
                  onDeleteIntegration={handleDeleteIntegrationFromPanel}
                  onNavigateToDatabase={(platformId) => {
                    setSelectedNode(null);
                    setNavigateToPlatformId(platformId);
                    setActiveTab('database');
                  }}
                  isMobile
                  activeLayer={activeLayer}
                  token={token}
                  onRefreshStack={() => fetchStack(selectedProperty)}
                  onQuickAddIntegration={handleQuickAddIntegration}
                />
              </SheetContent>
            </Sheet>
          )}
        </div>
        )
      ) : activeTab === 'database' ? (
        <TechDatabase
          key={`db-${tenantKey}`}
          token={token}
          role={role}
          isSuperAdmin={isSuperAdmin}
          navigateToPlatformId={navigateToPlatformId}
          onNavigationHandled={() => setNavigateToPlatformId(null)}
        />
      ) : activeTab === 'reports' ? (
        <Reports token={token} role={role} hasPermission={hasPermission} />
      ) : activeTab === 'admin' ? (
        role === 'admin' ? (
          <AdminPortal token={token} role={role} onSetTab={setActiveTab} onNavigate={handleTabChange} />
        ) : null
      ) : activeTab === 'ai' ? (
        hasPermission('nav.ai') ? <AiHub token={token} onNavigateReport={(reportId) => { setActiveTab('reports'); }} onNavigateTab={handleTabChange} hasPermission={hasPermission} /> : null
      ) : activeTab === 'users' ? (
        hasPermission('admin.users') ? <UserManagementV2 token={token} /> : null
      ) : activeTab === 'activity' ? (
        hasPermission('admin.activity_log') ? <ActivityLog token={token} /> : null
      ) : activeTab === 'groups' ? (
        hasPermission('admin.property_groups') ? <PropertyGroups token={token} role={role} /> : null
      ) : activeTab === 'property-profile' ? (
        <PropertyProfile propertyId={selectedProperty} token={token} role={role} />
      ) : activeTab === 'corporate-stack' ? (
        hasPermission('admin.corporate_stack') ? <CorporateStack token={token} role={role} /> : null
      ) : activeTab === 'onboarding' ? (
        hasPermission('admin.onboarding') ? <OnboardingWizard token={token} onComplete={(tid) => { setActiveTab('home'); setTenantKey(k => k + 1); }} /> : null
      ) : activeTab === 'approvals' ? (
        hasPermission('admin.approvals') ? <ApprovalQueue token={token} /> : null
      ) : activeTab === 'feature-manager' ? (
        <TenantFeatureManager token={token} />
      ) : activeTab === 'compare-view' ? (
        <PropertyCompareView token={token} properties={properties} />
      ) : activeTab === 'wizard' ? (
        (() => { setActiveTab('ai'); return null; })()
      ) : activeTab === 'documents' ? (
        <div className="flex-1 overflow-auto p-6 max-w-5xl mx-auto"><DocumentVault token={token} /></div>
      ) : activeTab === 'import' ? (
        <div className="flex-1 overflow-auto"><BulkImportWizard token={token} /></div>
      ) : activeTab === 'embed' ? (
        <div className="flex-1 overflow-auto p-6 max-w-3xl mx-auto"><EmbedCodeGenerator token={token} tenantId={currentTenantId} /></div>
      ) : activeTab === 'benchmarks' ? (
        <div className="flex-1 overflow-auto p-6 max-w-4xl mx-auto"><CompetitiveBenchmarks token={token} /></div>
      ) : activeTab === 'marketplace' ? (
        <div className="flex-1 overflow-auto p-6 max-w-4xl mx-auto"><VendorMarketplace token={token} /></div>
      ) : activeTab === 'templates' ? (
        <div className="flex-1 overflow-auto p-6 max-w-4xl mx-auto"><TemplateLibrary token={token} properties={properties} onApplied={() => fetchStack(selectedProperty)} /></div>
      ) : activeTab === 'diff' ? (
        <div className="flex-1 overflow-auto p-6 max-w-4xl mx-auto"><PropertyDiffView token={token} properties={properties} /></div>
      ) : activeTab === 'webhooks' ? (
        <div className="flex-1 overflow-auto p-6 max-w-3xl mx-auto"><WebhookSettings token={token} /></div>
      ) : activeTab === 'security' ? (
        <div className="flex-1 overflow-auto p-6 max-w-4xl mx-auto"><SecurityDashboard token={token} /></div>
      ) : activeTab === 'support-mobile' ? (
        <div className="flex-1 overflow-auto"><MobileSupportHub token={token} properties={properties} /></div>
      ) : activeTab === 'walkthrough' ? (
        <div className="flex-1 overflow-auto"><WalkthroughChecklist token={token} properties={properties} /></div>
      ) : activeTab === 'user-mgmt' ? (
        <div className="flex-1 overflow-auto"><MobileUserManagement token={token} /></div>
      ) : activeTab === 'financial' ? (
        (() => { setActiveTab('tools'); return null; })()
      ) : activeTab === 'tools' ? (
        <ToolsHub token={token} role={role} />
      ) : activeTab === 'super-admin' ? (
        <SuperAdminConsole token={token} />
      ) : activeTab === 'mobile-status' ? (
        <MobileStatusDashboard token={token} />
      ) : activeTab === 'mobile-properties' ? (
        <MobileMyProperties token={token} />
      ) : activeTab === 'mobile-support-dial' ? (
        <MobileSupportDial token={token} />
      ) : activeTab === 'mobile-incident' ? (
        <MobileIncidentReporter token={token} />
      ) : activeTab === 'mobile-scorecard' ? (
        <MobilePortfolioScorecard token={token} />
      ) : activeTab === 'mobile-spend-rings' ? (
        <MobileSpendRings token={token} />
      ) : activeTab === 'mobile-integ-health' ? (
        <MobileIntegrationHealth token={token} />
      ) : null}

      {/* Integration Editor Modal */}
      <IntegrationEditorModal
        open={integrationModalOpen}
        onOpenChange={setIntegrationModalOpen}
        sourceNode={editSourceNode}
        targetNode={integrationTarget}
        existingIntegration={existingIntegration}
        onSave={handleSaveIntegration}
        onDelete={handleDeleteIntegration}
        onSaveProductIntegration={async (data) => {
          setSaving(true);
          try {
            await axios.post(`${BACKEND_URL}/api/admin/product-integrations`, data, { headers });
            toast.success('Product integration created', {
              description: `${data.status} connection between solutions.`,
            });
            await fetchStack(selectedProperty);
            setIntegrationModalOpen(false);
            setEditSourceNode(null);
            setIntegrationTarget(null);
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create product integration');
          } finally {
            setSaving(false);
          }
        }}
        saving={saving}
        isPropertyView={selectedProperty && selectedProperty !== 'overall'}
      />

      {/* Global Search */}
      <GlobalSearch
        open={searchOpen}
        onOpenChange={setSearchOpen}
        token={token}
        onNavigate={(result) => {
          if (result.type === 'platform') {
            setNavigateToPlatformId(result.id);
            setActiveTab('database');
          } else if (result.type === 'property') {
            setSelectedProperty(result.id);
            setActiveTab('visualizer');
          } else if (result.type === 'product' || result.type === 'vendor') {
            setActiveTab('database');
          }
        }}
      />

      {/* 3D Visualization Overlay */}
      {show3D && stackData && (
        <Visualization3D stackData={stackData} onClose={() => setShow3D(false)} />
      )}

      {/* Command Palette */}
      <CommandPalette
        open={commandPaletteOpen}
        onOpenChange={setCommandPaletteOpen}
        token={token}
        properties={properties}
        stackData={stackData}
        selectedProperty={selectedProperty}
        onJumpToNode={(view) => handleJumpRecentView({ property_id: view.property_id, node_id: view.node_id })}
        onRefreshStack={() => fetchStack(selectedProperty)}
        onNavigate={(tab, propertyId) => {
          if (propertyId) { setSelectedProperty(propertyId); }
          handleTabChange(tab);
        }}
      />

      {/* Global keyboard shortcuts cheat sheet — opens on ? key */}
      <KeyboardShortcutsHelp />

      {/* Snapshot Create Dialog */}
      <SnapshotCreateDialog
        open={snapshotDialogOpen}
        onOpenChange={setSnapshotDialogOpen}
        token={token}
        stackData={stackData}
        selectedProperty={selectedProperty}
        propertyName={
          selectedProperty === 'overall'
            ? 'Overall Map'
            : (properties.find(p => p.id === selectedProperty)?.name || selectedProperty)
        }
        selectedNode={selectedNode}
        layoutMode={localStorage.getItem('lodgiflow_layout_mode') || 'category'}
        activeLayer={activeLayer}
        focusedCategory={focusedCategory}
        filters={{ show_used_only: showUsedOnly, hide_unconnected: hideUnconnected }}
      />

      {/* Node Context Menu (desktop) */}
      {contextMenu && (
        <NodeContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          node={contextMenu.node}
          onClose={() => setContextMenu(null)}
          onAction={(action, node) => {
            if (action === 'view-database') handleTabChange('database');
            else if (action === 'copy-id') { navigator.clipboard.writeText(node.id); toast.success('Copied: ' + node.id); }
            else if (action === 'open-support' && node.support_url) window.open(node.support_url, '_blank');
            else if (action === 'quick-fill' || action === 'reassign') setSelectedNode(node);
          }}
        />
      )}

      {/* Mobile Bottom Nav */}
      {isMobile && (
        <>
          <MobileBottomNav
            activeTab={activeTab}
            onTabChange={handleTabChange}
            moreOpen={mobileMoreOpen}
            onToggleMore={() => setMobileMoreOpen(v => !v)}
          />
          {mobileMoreOpen && (
            <MobileMoreMenu
              onTabChange={handleTabChange}
              onClose={() => setMobileMoreOpen(false)}
              hasPermission={hasPermission}
            />
          )}
        </>
      )}
    </div>
  );
}


function ResizeHandle({ onResize }) {
  const handleMouseDown = (e) => {
    e.preventDefault();
    let lastX = e.clientX;
    const handleMove = (moveEvent) => {
      const delta = moveEvent.clientX - lastX;
      lastX = moveEvent.clientX;
      onResize(delta);
    };
    const handleUp = () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  return (
    <div
      onMouseDown={handleMouseDown}
      className="w-1.5 shrink-0 cursor-col-resize hover:bg-primary/20 active:bg-primary/30 transition-colors relative group"
      data-testid="resize-handle"
    >
      <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-center">
        <div className="w-0.5 h-8 rounded-full bg-border group-hover:bg-primary/40 transition-colors" />
      </div>
    </div>
  );
}
